// Angular
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
// RxJS
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
// Services / config
import { HelperService } from './helper.service';
import { environment } from 'src/environments/environment';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
// Models
import {
  normalizeMessageHubResponse,
  GenerateDraftRequest,
  MessageAiDraftDto,
  MessageAuthorType,
  MessageCategory,
  MessageThreadDetail,
  MessageThreadListResponse,
  MessageThreadStatus,
  MessagingSettings,
  ResponseLogResponse,
  SendReplyRequest,
  SimulateInboundRequest,
  MailboxConnectionsResponse,
  SaveMailboxRequest,
  MessageChannel,
  PhoneAgentSettings
} from '../_models/messaging/message-hub.model';

const API_MESSAGE_HUB_URL = environment.baseUrl + '/MessageHub';

export interface ThreadListQuery {
  status?: MessageThreadStatus;
  storeType?: number;
  channel?: MessageChannel;
  category?: MessageCategory;
  search?: string;
  awaitingApproval?: boolean;
  overdueOnly?: boolean;
  resolvedTodayOnly?: boolean;
  skip?: number;
  take?: number;
}

export interface ResponseLogQuery {
  from?: string;
  to?: string;
  storeType?: number;
  category?: MessageCategory;
  authorType?: MessageAuthorType;
  skip?: number;
  take?: number;
}

@Injectable()
export class MessageHubService {
  constructor(private http: HttpClient,
              private helperService: HelperService,
              private toastr: ToastrService,
              private translate: TranslateService) {
  }

  linkOrder(threadId: number, orderId: number): Observable<any> {
    return this.http.post(`${API_MESSAGE_HUB_URL}/Threads/${threadId}/Order`, { orderId },
      { headers: this.helperService.getHTTPHeaders() }).pipe(catchError(this.handleError<any>('linkOrder')));
  }

  listThreads(query: ThreadListQuery): Observable<MessageThreadListResponse> {
    return this.http.get<MessageThreadListResponse>(`${API_MESSAGE_HUB_URL}/Threads`,
      { headers: this.helperService.getHTTPHeaders(), params: this.toParams(query) })
      .pipe(map(response => normalizeMessageHubResponse(response)), catchError(this.handleError<MessageThreadListResponse>('listThreads')));
  }

  getThread(id: number): Observable<MessageThreadDetail> {
    return this.http.get<MessageThreadDetail>(`${API_MESSAGE_HUB_URL}/Threads/${id}`,
      { headers: this.helperService.getHTTPHeaders() })
      .pipe(map(response => normalizeMessageHubResponse(response)), catchError(this.handleError<MessageThreadDetail>('getThread')));
  }

  generateDraft(threadId: number, request: GenerateDraftRequest): Observable<MessageAiDraftDto> {
    return this.http.post<MessageAiDraftDto>(`${API_MESSAGE_HUB_URL}/Threads/${threadId}/Draft`, request,
      { headers: this.helperService.getHTTPHeaders() })
      .pipe(map(response => normalizeMessageHubResponse(response)), catchError(this.handleError<MessageAiDraftDto>('generateDraft')));
  }

  sendReply(threadId: number, request: SendReplyRequest): Observable<unknown> {
    return this.http.post(`${API_MESSAGE_HUB_URL}/Threads/${threadId}/Reply`, request,
      { headers: this.helperService.getHTTPHeaders() })
      .pipe(map(response => normalizeMessageHubResponse(response)), catchError(this.handleError('sendReply')));
  }

  updateStatus(threadId: number, status: MessageThreadStatus, assignedToUserId?: string): Observable<unknown> {
    return this.http.post(`${API_MESSAGE_HUB_URL}/Threads/${threadId}/Status`, { status, assignedToUserId },
      { headers: this.helperService.getHTTPHeaders() })
      .pipe(map(response => normalizeMessageHubResponse(response)), catchError(this.handleError('updateStatus')));
  }

  getResponseLog(query: ResponseLogQuery): Observable<ResponseLogResponse> {
    return this.http.get<ResponseLogResponse>(`${API_MESSAGE_HUB_URL}/ResponseLog`,
      { headers: this.helperService.getHTTPHeaders(), params: this.toParams(query) })
      .pipe(map(response => normalizeMessageHubResponse(response)), catchError(this.handleError<ResponseLogResponse>('getResponseLog')));
  }

  getSettings(): Observable<MessagingSettings> {
    return this.http.get<MessagingSettings>(`${API_MESSAGE_HUB_URL}/Settings`,
      { headers: this.helperService.getHTTPHeaders() })
      .pipe(map(response => normalizeMessageHubResponse(response)), catchError(this.handleError<MessagingSettings>('getSettings')));
  }

  saveSettings(settings: Partial<MessagingSettings>): Observable<unknown> {
    return this.http.post(`${API_MESSAGE_HUB_URL}/Settings`, settings,
      { headers: this.helperService.getHTTPHeaders() })
      .pipe(map(response => normalizeMessageHubResponse(response)), catchError(this.handleError('saveSettings')));
  }

  listMailboxes(): Observable<MailboxConnectionsResponse> {
    return this.http.get<MailboxConnectionsResponse>(`${API_MESSAGE_HUB_URL}/Mailboxes`,
      { headers: this.helperService.getHTTPHeaders() })
      .pipe(map(response => normalizeMessageHubResponse(response)), catchError(this.handleError<MailboxConnectionsResponse>('listMailboxes')));
  }

  saveMailbox(request: SaveMailboxRequest): Observable<unknown> {
    return this.http.post(`${API_MESSAGE_HUB_URL}/Mailboxes`, request,
      { headers: this.helperService.getHTTPHeaders() })
      .pipe(map(response => normalizeMessageHubResponse(response)), catchError(this.handleError('saveMailbox')));
  }

  testMailbox(id: number): Observable<unknown> {
    return this.http.post(`${API_MESSAGE_HUB_URL}/Mailboxes/${id}/Test`, {},
      { headers: this.helperService.getHTTPHeaders() })
      .pipe(map(response => normalizeMessageHubResponse(response)), catchError(this.handleError('testMailbox')));
  }

  /** Polls one mailbox now instead of waiting for the scheduled sweep. */
  pollMailbox(id: number): Observable<{ fetched: number; ingested: number }> {
    return this.http.post<{ fetched: number; ingested: number }>(
      `${API_MESSAGE_HUB_URL}/Mailboxes/${id}/Poll`, {},
      { headers: this.helperService.getHTTPHeaders() })
      .pipe(map(response => normalizeMessageHubResponse(response)), catchError(this.handleError<{ fetched: number; ingested: number }>('pollMailbox')));
  }

  deleteMailbox(id: number): Observable<unknown> {
    return this.http.delete(`${API_MESSAGE_HUB_URL}/Mailboxes/${id}`,
      { headers: this.helperService.getHTTPHeaders() })
      .pipe(map(response => normalizeMessageHubResponse(response)), catchError(this.handleError('deleteMailbox')));
  }

  /** Feeds a message in as though the marketplace had relayed it — for trying the inbox before the
   *  mailbox DNS is live. */
  simulateInbound(request: SimulateInboundRequest): Observable<{ threadId: number; outcome: string; threadKey: string }> {
    return this.http.post<{ threadId: number; outcome: string; threadKey: string }>(
      `${API_MESSAGE_HUB_URL}/Threads/Simulate`, request,
      { headers: this.helperService.getHTTPHeaders() })
      .pipe(map(response => normalizeMessageHubResponse(response)), catchError(this.handleError<{ threadId: number; outcome: string; threadKey: string }>('simulateInbound')));
  }

  /** Drops undefined/empty entries so an unset filter is absent rather than sent as the string
   *  "undefined", which the model binder would reject. Takes a plain object rather than an indexed
   *  type so the query interfaces can be passed without declaring an index signature. */
  /** The call recording as audio. Fetched through the API, which holds the org's Twilio credentials. */
  getCallRecording(threadId: number): Observable<Blob> {
    return this.http.get(`${API_MESSAGE_HUB_URL}/Threads/${threadId}/Recording`,
      { headers: this.helperService.getHTTPHeaders(), responseType: 'blob' });
  }

  reviewReturnRequest(threadId: number, draftId: number, approve: boolean): Observable<unknown> {
    return this.http.post(`${API_MESSAGE_HUB_URL}/Threads/${threadId}/ReturnRequest`, { draftId, approve },
      { headers: this.helperService.getHTTPHeaders() })
      .pipe(catchError(this.handleError('reviewReturnRequest')));
  }

  getPhoneAgentSettings(): Observable<PhoneAgentSettings> {
    return this.http.get<PhoneAgentSettings>(`${API_MESSAGE_HUB_URL}/PhoneAgent`,
      { headers: this.helperService.getHTTPHeaders() })
      .pipe(catchError(this.handleError<PhoneAgentSettings>('getPhoneAgentSettings')));
  }

  savePhoneAgentSettings(settings: PhoneAgentSettings): Observable<{ twilioConnectedAt?: string }> {
    return this.http.post<{ twilioConnectedAt?: string }>(`${API_MESSAGE_HUB_URL}/PhoneAgent`, settings,
      { headers: this.helperService.getHTTPHeaders() })
      .pipe(catchError(this.handleError<{ twilioConnectedAt?: string }>('savePhoneAgentSettings')));
  }

  /** Checks the saved Twilio credentials and points the number's webhooks at InventoryAhead. */
  connectPhoneAgent(): Observable<{ twilioConnectedAt: string; webhookUrl: string }> {
    return this.http.post<{ twilioConnectedAt: string; webhookUrl: string }>(`${API_MESSAGE_HUB_URL}/PhoneAgent/Connect`, {},
      { headers: this.helperService.getHTTPHeaders() })
      .pipe(catchError(this.handleError<{ twilioConnectedAt: string; webhookUrl: string }>('connectPhoneAgent')));
  }

  /** Adds a made-up call so the Calls view can be tried before Twilio is connected. */
  simulateCall(): Observable<{ threadId: number }> {
    return this.http.post<{ threadId: number }>(`${API_MESSAGE_HUB_URL}/Calls/Simulate`, {},
      { headers: this.helperService.getHTTPHeaders() })
      .pipe(catchError(this.handleError<{ threadId: number }>('simulateCall')));
  }

  private toParams(query: object): HttpParams {
    let params = new HttpParams();
    Object.entries(query || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });
    return params;
  }

  private handleError<T>(operation = 'operation') {
    return (error: HttpErrorResponse): Observable<T> => {
      switch (error.status) {
        case 401:
          window.location.href = '/login';
          break;
        case 422:
          this.toastr.error(error.error?.reason || error.error?.Message || error.error?.message
            || this.translate.instant('GENERAL.MESSAGES.ERROR'));
          break;
        // 400 carries a specific, actionable refusal from the handlers - "this order does not match the
        // customer's email", say. Folding it into the generic message threw that away.
        case 400:
        case 404:
          this.toastr.error(error.error?.Message || error.error?.message
            || this.translate.instant('GENERAL.MESSAGES.ERROR'));
          break;
        default:
          this.toastr.error(this.translate.instant('GENERAL.MESSAGES.ERROR'));
      }
      return throwError(error);
    };
  }
}
